import React, { Component } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import Modal from 'components/Modal/Modal';
import Button from 'components/Button';
import Preloader from 'components/Preloader';
import ReactSelect from 'components/Select/ReactSelect';
// utils
import { fetchFromAPI } from 'utils/api';
import { showToastrMessage } from 'utils';
import {
  IMPORTED_SENSOR_TYPES, isPublicApiIntegration, fetchSitesForOrg, toastBarnToolsErrors,
} from './barnToolsConstants';
import classnames from 'classnames/bind';
// styles
import styles from './ManageSensorsModal.scss';
import asyncSelectStyles from 'components/AsyncMultiSelect/AsyncMultiSelect.module.scss';

const cn = classnames.bind(styles);
const cnAsync = classnames.bind(asyncSelectStyles);

const LocalStringMultiSelect = ({
  values = [],
  onSelect,
  onRemove,
  options = [],
  className,
  labelClassName,
  scrollable,
  labels = {},
}) => {
  const labelFor = (value) => {
    const sensor = labels[value];

    return sensor ? `${sensor.name || value} — ${sensor.typeName}` : value;
  };

  return (
    <div className={cnAsync('multi-select', className)}>
      <div>
        <ReactSelect
          className="input"
          placeholder={<FormattedMessage id="component.barnTools.selectSensors" />}
          clearable={false}
          options={options
            .filter((o) => !values.includes(o))
            .map((o) => ({ value: o, label: labelFor(o) }))}
          onChange={(opt) => opt && onSelect(opt.value)}
          onFocus={() => {}}
          value={null}
        />
      </div>
      <div className={cnAsync('selected-box', { scrollable })}>
        {values?.map((value, index) => (
          <div key={value || index} className={cnAsync('selected-item')}>
            <div className={cnAsync(labelClassName)}>{labelFor(value)}</div>
            <i
              className={`${cnAsync('remove-btn')} fa fa-times`}
              onClick={() => onRemove(value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

LocalStringMultiSelect.propTypes = {
  values: T.arrayOf(T.string),
  onSelect: T.func,
  onRemove: T.func,
  options: T.arrayOf(T.string),
  className: T.string,
  labelClassName: T.string,
  scrollable: T.bool,
  labels: T.object,
};

class ManageSensorsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      isSaving: false,
      selectedSensors: [],
      availableSensors: [],
      sensorInputValue: '',
      // serial => {name, typeName} for devices reading from the Public API
      sensorLabels: {},
    };
  }

  componentDidMount() {
    this.loadDeviceData();
  }

  isPublicApi = () => isPublicApiIntegration(this.props.integration);

  loadDeviceData = () => {
    const { integration } = this.props;

    if (this.isPublicApi()) {
      this.loadInventorySensors();
      return;
    }

    this.setState({
      availableSensors: Array.isArray(integration.available_sensors) ? integration.available_sensors : [],
      selectedSensors: Array.isArray(integration.sensors) ? integration.sensors : [],
      isLoading: false,
    });
  };

  // A device on the Public API points at sensor serial numbers, so the choices
  // come from what BarnTools reports for its barn rather than from the record.
  loadInventorySensors = () => {
    const { integration, companyId } = this.props;
    const companyIdToUse = companyId || integration.company_id;

    this.setState({ isLoading: true });

    fetchSitesForOrg(fetchFromAPI, companyIdToUse, { org: integration.bt_org_name })
      .then((resources) => {
        const site = (resources || []).find((s) => s.id === integration.bt_site_id);
        const barns = site?.barns || [];
        const barn = barns.find((b) => b.id === integration.bt_barn_id) || (barns.length === 1 ? barns[0] : null);
        const sensors = (barn?.sensors || []).filter((s) => IMPORTED_SENSOR_TYPES.includes(s.typeName));

        this.setState({
          availableSensors: sensors.map((s) => s.serialNumber),
          sensorLabels: sensors.reduce((acc, s) => ({ ...acc, [s.serialNumber]: s }), {}),
          selectedSensors: Array.isArray(integration.sensor_serials) ? integration.sensor_serials : [],
          isLoading: false,
        });
      })
      .catch(() => {
        showToastrMessage('component.toastr.barnTools.error.inventoryFailed', 'error');
        this.setState({ isLoading: false });
      });
  };

  handleSensorSelectOption = (sensorName) => {
    const { selectedSensors } = this.state;
    if (sensorName && !selectedSensors.includes(sensorName)) {
      this.setState({ selectedSensors: [...selectedSensors, sensorName] });
    }
  };

  handleSensorInputChange = (e) => {
    this.setState({ sensorInputValue: e.target.value });
  };

  handleSensorAdd = () => {
    const { sensorInputValue, availableSensors, selectedSensors } = this.state;
    const canAdd = sensorInputValue
      && availableSensors.includes(sensorInputValue)
      && !selectedSensors.includes(sensorInputValue);
    if (canAdd) {
      this.setState((prevState) => ({
        selectedSensors: [...prevState.selectedSensors, sensorInputValue],
        sensorInputValue: '',
      }));
    }
  };

  handleSensorRemove = (sensor) => {
    this.setState((prevState) => ({
      selectedSensors: prevState.selectedSensors.filter((s) => s !== sensor)
    }));
  };

  handleCancel = () => {
    const { closeModal } = this.props;
    closeModal();
  };

  handleSave = () => {
    const { integration, onSensorsUpdate, closeModal } = this.props;
    const { selectedSensors } = this.state;

    this.setState({ isSaving: true });

    const updateData = this.isPublicApi()
      ? { sensor_serials: selectedSensors }
      : { sensors: selectedSensors };
    const companyId = this.props.companyId || integration.company_id;
    if (!companyId) {
      showToastrMessage('component.toastr.barnTools.error.sensorsUpdateFailed', 'error');
      this.setState({ isSaving: false });
      return;
    }

    fetchFromAPI(`/admin/companies/${companyId}/barn_tools_integrations/${integration.id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    })
      .then(() => {
        showToastrMessage('component.toastr.barnTools.success.sensorsUpdated');
        onSensorsUpdate();
        closeModal();
      })
      .catch((error) => {
        toastBarnToolsErrors(error, 'component.toastr.barnTools.error.sensorsUpdateFailed');
      })
      .finally(() => {
        this.setState({ isSaving: false });
      });
  };

  getSensorOptions = () => {
    const { availableSensors } = this.state;

    return availableSensors.map((sensor) => ({
      value: sensor.id,
      label: sensor.name,
      ...sensor
    }));
  };

  render() {
    const { integration } = this.props;
    const { isLoading, isSaving, selectedSensors } = this.state;

    if (!integration) {
      return (
        <Modal title={<FormattedMessage id="component.barnTools.manageSensors" />}>
          <section className="modal-body">
            <div className="error-message">
              <FormattedMessage id="component.barnTools.error.noIntegration" />
            </div>
          </section>
        </Modal>
      );
    }

    if (isLoading) {
      return (
        <Modal title={<FormattedMessage id="component.barnTools.manageSensors" />}>
          <section className="modal-body">
            <Preloader isActive />
          </section>
        </Modal>
      );
    }

    return (
      <Modal title={<FormattedMessage id="component.barnTools.manageSensors" />}>
        <section className="modal-body">
          <div className={cn('sensors-modal')}>
            <div className="form-group">
              <label>
                <FormattedMessage id="component.barnTools.farm" />
              </label>
              <input
                type="text"
                className="form-control"
                value={integration.farm_name || 'N/A'}
                disabled
                readOnly
              />
            </div>

            <div className="form-group">
              <label>
                <FormattedMessage id="component.barnTools.barn" />
              </label>
              <input
                type="text"
                className="form-control"
                value={integration.barn_name || 'N/A'}
                disabled
                readOnly
              />
            </div>

            {this.isPublicApi() ? (
              <div className="form-group">
                <label>
                  <FormattedMessage id="component.barnTools.site" />
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={integration.bt_site_id || 'N/A'}
                  disabled
                  readOnly
                />
              </div>
            ) : (
              <React.Fragment>
                <div className="form-group">
                  <label>
                    <FormattedMessage id="component.barnTools.gatewayNumber" />
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={integration.gateway_number || 'N/A'}
                    disabled
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label>
                    <FormattedMessage id="component.barnTools.phone" />
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={integration.phone || 'N/A'}
                    disabled
                    readOnly
                  />
                </div>
              </React.Fragment>
            )}

            <div className="form-group">
              <label>
                <FormattedMessage id="component.barnTools.sensors" />
              </label>
              <LocalStringMultiSelect
                values={selectedSensors}
                onSelect={this.handleSensorSelectOption}
                onRemove={this.handleSensorRemove}
                options={this.state.availableSensors}
                labels={this.state.sensorLabels}
                className={cn('sensors-select')}
                scrollable
              />
            </div>
          </div>
        </section>
        <section className="modal-footer text-right">
          <Button
            onClick={this.handleCancel}
            light
            className="mr-10"
          >
            <FormattedMessage id="general.button.cancel" />
          </Button>
          <Button
            onClick={this.handleSave}
            primary
            disabled={isSaving}
          >
            {isSaving ? (
              <FormattedMessage id="general.button.saving" />
            ) : (
              <FormattedMessage id="general.button.save" />
            )}
          </Button>
        </section>
      </Modal>
    );
  }
}

ManageSensorsModal.propTypes = {
  integration: T.object.isRequired,
  onSensorsUpdate: T.func.isRequired,
  closeModal: T.func.isRequired,
  companyId: T.number,
};

const mapStateToProps = (state) => ({
  companyId: state.auth.user?.current_company?.id,
});

export default connect(mapStateToProps, { closeModal })(ManageSensorsModal);
